import type { EventEmitter } from 'node:events';
import type * as RDF from '@rdfjs/types';
import type { AsyncIterator } from 'asynciterator';
import { wrap } from 'asynciterator';
import { DataFactory } from 'rdf-data-factory';
import type { QuadTermName } from 'rdf-terms';
import {
  matchPattern,
  QUAD_TERM_NAMES,
} from 'rdf-terms';
import { BindingsIterator } from './BindingsIterator';
import { BindingsProducer, FilteringBindingsProducer } from './BindingsProducer';
import { DatasetCoreWrapper } from './dataset/DatasetCoreWrapper';
import type { ITermDictionary } from './dictionary/ITermDictionary';
import { TermDictionaryNumberRecordFullTerms } from './dictionary/TermDictionaryNumberRecordFullTerms';
import { TermDictionaryQuotedIndexed } from './dictionary/TermDictionaryQuotedIndexed';
import type { IRdfStoreIndex } from './index/IRdfStoreIndex';
import { RdfStoreIndexBTree } from './index/RdfStoreIndexBTree';
import { RdfStoreIndexNestedMapQuoted } from './index/RdfStoreIndexNestedMapQuoted';
import type { IRdfStoreOptions } from './IRdfStoreOptions';
import {
  encodeAndExtendFilters,
  encodeOptionalTerms,
  getBestIndexLookupTable,
  getBestIndexTerms,
  getComponentOrderPermutation,
  getIndexMatchTermsPath,
  orderQuadComponentsPermutation,
  quadToPattern,
} from './OrderUtils';
import type { EncodedQuadTerms, QuadPatternTerms } from './PatternTerm';
import { TermOrder } from './TermOrder';

/**
 * An RDF store allows quads to be stored and fetched, based on one or more customizable indexes.
 */
export class RdfStore<TE = any, TQ extends RDF.BaseQuad = RDF.Quad> implements RDF.Store<TQ> {
  // eslint-disable-next-line ts/naming-convention
  public static readonly DEFAULT_INDEX_COMBINATIONS: QuadTermName[][] = [
    [ 'graph', 'subject', 'predicate', 'object' ],
    [ 'graph', 'predicate', 'object', 'subject' ],
    [ 'graph', 'object', 'subject', 'predicate' ],
  ];

  public readonly options: IRdfStoreOptions<TE, TQ>;
  public readonly dataFactory: RDF.DataFactory<TQ>;
  public readonly dictionary: ITermDictionary<TE>;
  public readonly indexesWrapped: IRdfStoreIndexWrapped<TE>[];
  private readonly indexesWrappedComponentOrders: QuadTermName[][];
  public readonly features = { quotedTripleFiltering: true, indexNodes: false, indexDistinctTerms: true };
  private readonly indexNodes: Map<TE, Set<TE>> | undefined;
  /**
   * Whether or not the dictionary and all indexes support quoted triple patterns.
   * This is invariant for the lifetime of the store, so it is determined once upon construction.
   */
  private readonly indexesSupportQuotedPatterns: boolean;
  /**
   * Lookup table from a bitmask of defined quad pattern components to the best index.
   */
  private readonly bestIndexLookup: Uint8Array;
  /**
   * If every index can insert a batch of quads at once, in which case imports are batched.
   */
  private readonly batchable: boolean;

  // eslint-disable-next-line ts/naming-convention
  private _size = 0;

  public constructor(options: IRdfStoreOptions<TE, TQ>) {
    this.options = options;
    this.dataFactory = options.dataFactory;
    this.dictionary = options.dictionary;
    // Ordered indexes must agree on the order of terms, so they share one term order.
    this.indexesWrapped = RdfStore.constructIndexesWrapped({
      ...options,
      termOrder: options.termOrder ??
        new TermOrder(<ITermDictionary<number>> <unknown> options.dictionary, options.termComparator),
    });
    this.indexesWrappedComponentOrders = this.indexesWrapped.map(indexThis => indexThis.componentOrder);
    this.indexNodes = options.indexNodes ? new Map() : undefined;
    this.features.indexNodes = Boolean(options.indexNodes);
    this.indexesSupportQuotedPatterns = Boolean(this.dictionary.features.quotedTriples) &&
      this.indexesWrapped.every(wrapped => wrapped.index.features.quotedTripleFiltering);
    this.bestIndexLookup = getBestIndexLookupTable(this.indexesWrappedComponentOrders);
    this.batchable = this.indexesWrapped.every(wrapped => wrapped.index.setAll !== undefined);
  }

  /**
   * Create an RDF store whose indexes keep their quads sorted, so that scans produce ordered results
   * and can skip ahead. Stores quads in GSPO, GPOS, and GOSP order by default, like
   * {@link RdfStore#createDefault}.
   * @param options Optional settings.
   * @param options.termComparator The order to keep terms in, which defaults to {@link defaultTermComparator}.
   * @param options.indexCombinations The component orders of the indexes to create.
   * @param options.nodes If an index of nodes (subjects or objects) must be maintained.
   */
  public static createOrdered(options: {
    termComparator?: (left: RDF.Term, right: RDF.Term) => number;
    indexCombinations?: QuadTermName[][];
    nodes?: true;
  } = {}): RdfStore<number> {
    return new RdfStore<number>({
      indexCombinations: options.indexCombinations ?? RdfStore.DEFAULT_INDEX_COMBINATIONS,
      indexConstructor: subOptions => new RdfStoreIndexBTree(subOptions),
      indexNodes: options.nodes,
      dictionary: new TermDictionaryQuotedIndexed(new TermDictionaryNumberRecordFullTerms()),
      dataFactory: new DataFactory(),
      termComparator: options.termComparator,
    });
  }

  /**
   * Create an RDF store with default settings.
   * Concretely, this store stores triples in GSPO, GPOS, and GOSP order,
   * and makes use of in-memory number dictionary encoding.
   * @param nodes If an index of nodes (subjects or objects) must be maintained.
   */
  public static createDefault(nodes?: true): RdfStore<number> {
    return new RdfStore<number>({
      indexCombinations: RdfStore.DEFAULT_INDEX_COMBINATIONS,
      indexConstructor: subOptions => new RdfStoreIndexNestedMapQuoted(subOptions),
      indexNodes: nodes,
      dictionary: new TermDictionaryQuotedIndexed(new TermDictionaryNumberRecordFullTerms()),
      dataFactory: new DataFactory(),
    });
  }

  /**
   * Internal helper to create index objects.
   * @param options The RDF store options object.
   */
  public static constructIndexesWrapped<TE, TQ extends RDF.BaseQuad = RDF.Quad>(
    options: IRdfStoreOptions<TE, TQ>,
  ): IRdfStoreIndexWrapped<TE>[] {
    const indexes: IRdfStoreIndexWrapped<TE>[] = [];
    if (options.indexCombinations.length === 0) {
      throw new Error('At least one index combination is required');
    }
    for (const componentOrder of options.indexCombinations) {
      if (!RdfStore.isCombinationValid(componentOrder)) {
        throw new Error(`Invalid index combination: ${componentOrder.join(',')}`);
      }
      indexes.push({
        index: options.indexConstructor(options),
        componentOrder,
        componentOrderPermutation: getComponentOrderPermutation(componentOrder),
        // eslint-disable-next-line ts/no-unsafe-assignment
        componentOrderInverse: <any>Object.fromEntries(componentOrder.map((value, key) => [ value, key ])),
      });
    }
    return indexes;
  }

  /**
   * Check if a given quad term order is valid.
   * @param combination A quad term order.
   */
  public static isCombinationValid(combination: QuadTermName[]): boolean {
    for (const quadTermName of QUAD_TERM_NAMES) {
      if (!combination.includes(quadTermName)) {
        return false;
      }
    }
    return combination.length === 4;
  }

  /**
   * Every order a scan of this store could come back in, one per index, as component names.
   *
   * A consumer that wants results in a particular order has to know whether this store can produce it
   * before it plans around one. The order a given pattern actually gets is the entry for the index that
   * serves it, with the components the pattern binds removed, since those do not vary across the scan.
   *
   * Only ordered indexes, such as {@link RdfStoreIndexBTree}, are listed: the others iterate in insertion
   * order, so a scan of them has no useful order at all.
   */
  public get indexOrders(): QuadTermName[][] {
    return this.indexesWrapped
      .filter(indexWrapped => indexWrapped.index.termOrder !== undefined)
      .map(indexWrapped => [ ...indexWrapped.componentOrder ]);
  }

  /**
   * The components a scan of the given index varies over, in the order it will produce them.
   *
   * Components fixed by the pattern are constant across the scan and therefore left out. Only
   * reported for an ordered index, since an unordered one produces no useful order.
   * @param index The index being scanned.
   * @param componentOrder The component order of the index being scanned.
   * @param subject The subject of the pattern.
   * @param predicate The predicate of the pattern.
   * @param object The object of the pattern.
   * @param graph The graph of the pattern.
   */
  private resultOrderOf(
    index: IRdfStoreIndex<TE, boolean>,
    componentOrder: QuadTermName[],
    subject: RDF.Term,
    predicate: RDF.Term,
    object: RDF.Term,
    graph: RDF.Term,
  ): QuadTermName[] | undefined {
    if (index.termOrder === undefined) {
      return undefined;
    }
    const byComponent: Record<string, RDF.Term> = { subject, predicate, object, graph };
    return componentOrder.filter(component => byComponent[component].termType === 'Variable');
  }

  /**
   * Build the callback that lets a scan skip ahead to a term, or undefined when it cannot.
   *
   * Skipping is only sound on an ordered index.
   * @param index The index being scanned.
   * @param componentOrder The component order of that index.
   * @param producer The producer reading that index.
   */
  private createSeeker(
    index: IRdfStoreIndex<TE, boolean>,
    componentOrder: QuadTermName[],
    producer: BindingsProducer<TE>,
  ): ((component: QuadTermName, term: RDF.Term) => void) | undefined {
    const termOrder = index.termOrder;
    if (termOrder === undefined) {
      return undefined;
    }
    return (component: QuadTermName, term: RDF.Term): void => {
      // Every component order names all four components, so this always finds one. Seeking a
      // component the pattern binds is pointless rather than an error: that key is fixed, so the
      // scan either carries on untouched or runs out.
      // The sought term does not have to be in the store: keys before it are exactly those with a lower label.
      const target = termOrder.lowerBound(term);
      producer.seek(componentOrder.indexOf(component), key => termOrder.label(<number> <unknown> key) < target);
    };
  }

  /**
   * The number of quads in this store.
   */
  public get size(): number {
    return this._size;
  }

  /**
   * Add a quad to the store.
   * @param quad An RDF quad.
   * @return boolean If the quad was not yet present in the index.
   */
  public addQuad(quad: TQ): boolean {
    const quadEncoded = [
      this.dictionary.encode(quad.subject),
      this.dictionary.encode(quad.predicate),
      this.dictionary.encode(quad.object),
      this.dictionary.encode(quad.graph),
    ];
    let newQuad = false;
    for (const indexWrapped of this.indexesWrapped) {
      // Before sending the quad to the index, make sure its components are ordered corresponding to the index's order.
      newQuad = indexWrapped.index
        .set(<EncodedQuadTerms<TE>>
          orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadEncoded), true);
      if (!newQuad) {
        // All indexes hold the same quads, so if the first one already had it, the others have it too.
        break;
      }
    }
    if (newQuad) {
      this._size++;

      // If we're indexing nodes, add subject and object to this index.
      if (this.indexNodes) {
        let graphIndex = this.indexNodes.get(quadEncoded[3]);
        if (!graphIndex) {
          graphIndex = new Set();
          this.indexNodes.set(quadEncoded[3], graphIndex);
        }
        graphIndex.add(quadEncoded[0]);
        graphIndex.add(quadEncoded[2]);
      }

      return true;
    }
    return false;
  }

  /**
   * Remove a quad from the store.
   * @param quad An RDF quad.
   * @return boolean If the quad was present in the index.
   */
  public removeQuad(quad: TQ): boolean {
    const quadEncoded = [
      this.dictionary.encodeOptional(quad.subject),
      this.dictionary.encodeOptional(quad.predicate),
      this.dictionary.encodeOptional(quad.object),
      this.dictionary.encodeOptional(quad.graph),
    ];

    // We can quickly return false if the quad is not present in the dictionary

    if (quadEncoded[0] === undefined || quadEncoded[1] === undefined ||
      quadEncoded[2] === undefined || quadEncoded[3] === undefined) {
      return false;
    }

    let wasPresent = false;
    for (const indexWrapped of this.indexesWrapped) {
      // Before sending the quad to the index, make sure its components are ordered corresponding to the index's order.
      wasPresent = indexWrapped.index
        .remove(<EncodedQuadTerms<TE>>
          orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadEncoded));
      if (!wasPresent) {
        break;
      }
    }
    if (wasPresent) {
      this._size--;

      // If we're indexing nodes, remove subject and object from this index if they are not present anymore.
      if (this.indexNodes) {
        const graphIndex = this.indexNodes.get(quadEncoded[3]!)!;
        if (!this.readQuads(quad.subject, undefined, undefined, quad.graph).next().value) {
          graphIndex.delete(quadEncoded[0]!);
        }
        if (!this.readQuads(undefined, undefined, quad.object, quad.graph).next().value) {
          graphIndex.delete(quadEncoded[2]!);
        }
        if (graphIndex.size === 0) {
          this.indexNodes.delete(quadEncoded[3]!);
        }
      }

      return true;
    }
    return false;
  }

  /**
   * Removes all streamed quads.
   * @param stream A stream of quads
   */
  public remove(stream: RDF.Stream<TQ>): EventEmitter {
    // eslint-disable-next-line ts/no-unsafe-argument
    stream.on('data', quad => this.removeQuad(quad));
    return stream;
  }

  /**
   * All quads matching the pattern will be removed.
   * @param subject The optional subject.
   * @param predicate The optional predicate.
   * @param object The optional object.
   * @param graph The optional graph.
   */
  public removeMatches(
    subject?: RDF.Term | null | undefined,
    predicate?: RDF.Term | null | undefined,
    object?: RDF.Term | null | undefined,
    graph?: RDF.Term | null | undefined,
  ): EventEmitter {
    return this.remove(this.match(subject, predicate, object, graph));
  }

  /**
   * Deletes the given named graph.
   * @param graph The graph term or string to match.
   */
  public deleteGraph(graph: string | TQ['graph']): EventEmitter {
    if (typeof graph === 'string') {
      graph = this.dataFactory.namedNode(graph);
    }
    return this.removeMatches(undefined, undefined, undefined, graph);
  }

  /**
   * Import the given stream of quads into the store.
   * @param stream A stream of RDF quads.
   */
  public import(stream: RDF.Stream<TQ>): EventEmitter {
    if (!this.batchable) {
      stream.on('data', (quad: TQ) => this.addQuad(quad));
      return stream;
    }

    // Every index can insert a batch at once, so quads are encoded as they come in, and inserted when
    // the stream ends. This listener is attached before the caller can attach its own, so the quads are
    // in the store by the time the caller sees the end.
    let buffer = new Int32Array(4096);
    let count = 0;
    stream.on('data', (quad: TQ) => {
      if ((count + 1) * 4 > buffer.length) {
        const grown = new Int32Array(buffer.length * 2);
        grown.set(buffer);
        buffer = grown;
      }
      this.encodeInto(quad, buffer, count * 4);
      count++;
    });
    stream.on('end', () => {
      this.addEncodedQuads(buffer, count);
    });
    return stream;
  }

  /**
   * Add many quads at once.
   *
   * If every index supports it, the quads are inserted as a single batch, which sorts them once per
   * index rather than inserting them one by one. Otherwise, this is the same as adding them one by one.
   * @param quads RDF quads.
   * @return number The number of quads that were not yet present.
   */
  public addQuads(quads: Iterable<TQ>): number {
    if (!this.batchable) {
      let added = 0;
      for (const quad of quads) {
        if (this.addQuad(quad)) {
          added++;
        }
      }
      return added;
    }
    const quadsArray = Array.isArray(quads) ? <TQ[]> quads : [ ...quads ];
    const buffer = new Int32Array(quadsArray.length * 4);
    for (const [ i, quad ] of quadsArray.entries()) {
      this.encodeInto(quad, buffer, i * 4);
    }
    return this.addEncodedQuads(buffer, quadsArray.length);
  }

  private encodeInto(quad: TQ, buffer: Int32Array, offset: number): void {
    buffer[offset] = <number> <unknown> this.dictionary.encode(quad.subject);
    buffer[offset + 1] = <number> <unknown> this.dictionary.encode(quad.predicate);
    buffer[offset + 2] = <number> <unknown> this.dictionary.encode(quad.object);
    buffer[offset + 3] = <number> <unknown> this.dictionary.encode(quad.graph);
  }

  /**
   * Insert a batch of encoded quads into every index.
   * @param buffer Encoded quads in SPOG order, four entries per quad.
   * @param count The number of quads in the buffer.
   * @return number The number of quads that were not yet present.
   */
  private addEncodedQuads(buffer: Int32Array, count: number): number {
    let added = 0;
    const permuted = new Int32Array(count * 4);
    for (const [ i, indexWrapped ] of this.indexesWrapped.entries()) {
      const permutation = indexWrapped.componentOrderPermutation;
      for (let offset = 0; offset < count * 4; offset += 4) {
        permuted[offset] = buffer[offset + permutation[0]];
        permuted[offset + 1] = buffer[offset + permutation[1]];
        permuted[offset + 2] = buffer[offset + permutation[2]];
        permuted[offset + 3] = buffer[offset + permutation[3]];
      }
      const indexAdded = indexWrapped.index.setAll!(permuted, count);
      if (i === 0) {
        added = indexAdded;
      }
    }
    this._size += added;

    if (this.indexNodes) {
      for (let offset = 0; offset < count * 4; offset += 4) {
        const graph = <TE> <unknown> buffer[offset + 3];
        let graphIndex = this.indexNodes.get(graph);
        if (!graphIndex) {
          graphIndex = new Set();
          this.indexNodes.set(graph, graphIndex);
        }
        graphIndex.add(<TE> <unknown> buffer[offset]);
        graphIndex.add(<TE> <unknown> buffer[offset + 2]);
      }
    }
    return added;
  }

  /**
   * Determine the best index for the given quad pattern.
   *
   * The best index only depends on which of the four components are defined,
   * so this is a lookup in a table that was precomputed upon construction.
   *
   * @param quadComponents A quad pattern.
   */
  private getBestIndexWrapped(quadComponents: QuadPatternTerms): IRdfStoreIndexWrapped<TE> {
    return this.indexesWrapped[this.bestIndexLookup[
      (quadComponents[0] === undefined ? 0 : 1) |
      (quadComponents[1] === undefined ? 0 : 2) |
      (quadComponents[2] === undefined ? 0 : 4) |
      (quadComponents[3] === undefined ? 0 : 8)
    ]];
  }

  /**
   * Returns a generator producing all quads matching the pattern.
   * @param subject The optional subject.
   * @param predicate The optional predicate.
   * @param object The optional object.
   * @param graph The optional graph.
   */
  public* readQuads(
    subject?: RDF.Term | null,
    predicate?: RDF.Term | null,
    object?: RDF.Term | null,
    graph?: RDF.Term | null,
  ): IterableIterator<TQ> {
    // Construct a quad pattern array
    const [ quadComponents, requireQuotedTripleFiltering ] =
      quadToPattern(subject, predicate, object, graph, this.indexesSupportQuotedPatterns);

    // Determine the best index for this pattern
    const indexWrapped = this.getBestIndexWrapped(quadComponents);

    // Re-order the quad pattern based on this best index's component order
    const quadComponentsOrdered = <QuadPatternTerms>
      orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadComponents);

    // Hoist the de-ordering offsets and the data factory out of the loop below.
    const componentOrderInverse = indexWrapped.componentOrderInverse;
    const subjectI = componentOrderInverse.subject;
    const predicateI = componentOrderInverse.predicate;
    const objectI = componentOrderInverse.object;
    const graphI = componentOrderInverse.graph;
    const dataFactory = this.dataFactory;

    // Call the best index's find method.

    for (const decomposedQuad of indexWrapped.index.find(quadComponentsOrdered)) {
      // De-order the resulting quad components into the normal SPOG order for quad creation.
      const quad = dataFactory.quad(
        decomposedQuad[subjectI],
        decomposedQuad[predicateI],
        decomposedQuad[objectI],
        decomposedQuad[graphI],
      );
      if (requireQuotedTripleFiltering) {
        if (matchPattern(quad, subject!, predicate!, object!, graph!)) {
          yield quad;
        }
      } else {
        yield quad;
      }
    }
  }

  /**
   * Returns an array containing all quads matching the pattern.
   * @param subject The optional subject.
   * @param predicate The optional predicate.
   * @param object The optional object.
   * @param graph The optional graph.
   */
  public getQuads(
    subject?: RDF.Term | null,
    predicate?: RDF.Term | null,
    object?: RDF.Term | null,
    graph?: RDF.Term | null,
  ): TQ[] {
    // This intentionally does not delegate to readQuads:
    // spreading a generator into an array costs an extra generator resume
    // and an iterator result object per quad, which is significant for large result sets.

    // Construct a quad pattern array
    const [ quadComponents, requireQuotedTripleFiltering ] =
      quadToPattern(subject, predicate, object, graph, this.indexesSupportQuotedPatterns);

    // Determine the best index for this pattern
    const indexWrapped = this.getBestIndexWrapped(quadComponents);

    // Re-order the quad pattern based on this best index's component order
    const quadComponentsOrdered = <QuadPatternTerms>
      orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadComponents);

    // Hoist the de-ordering offsets and the data factory out of the loop below.
    const componentOrderInverse = indexWrapped.componentOrderInverse;
    const subjectI = componentOrderInverse.subject;
    const predicateI = componentOrderInverse.predicate;
    const objectI = componentOrderInverse.object;
    const graphI = componentOrderInverse.graph;
    const dataFactory = this.dataFactory;

    // Call the best index's find method.
    const quads: TQ[] = [];
    for (const decomposedQuad of indexWrapped.index.find(quadComponentsOrdered)) {
      // De-order the resulting quad components into the normal SPOG order for quad creation.
      const quad = dataFactory.quad(
        decomposedQuad[subjectI],
        decomposedQuad[predicateI],
        decomposedQuad[objectI],
        decomposedQuad[graphI],
      );
      if (!requireQuotedTripleFiltering || matchPattern(quad, subject!, predicate!, object!, graph!)) {
        quads.push(quad);
      }
    }
    return quads;
  }

  /**
   * Returns a stream that produces all quads matching the pattern.
   * @param subject The optional subject.
   * @param predicate The optional predicate.
   * @param object The optional object.
   * @param graph The optional graph.
   */
  public match(
    subject?: RDF.Term | null,
    predicate?: RDF.Term | null,
    object?: RDF.Term | null,
    graph?: RDF.Term | null,
  ): RDF.Stream<TQ> & AsyncIterator<TQ> {
    return wrap(this.readQuads(subject, predicate, object, graph));
  }

  /**
   * Prepare the production of bindings for the given quad pattern.
   *
   * This determines the best index, encodes the pattern, works out which components must be
   * bound, and detects whether any post-filtering is required.
   * All of this only depends on the pattern, so it is done once per lookup instead of per result.
   *
   * @param bindingsFactory The factory to use for creating bindings.
   * @param subject The subject, which can be a variable.
   * @param predicate The predicate, which can be a variable.
   * @param object The object, which can be a variable.
   * @param graph The graph, which can be a variable.
   * @return A producer of bindings, or undefined if the pattern can not produce any results.
   */
  private prepareBindings(
    bindingsFactory: RDF.BindingsFactory,
    subject: RDF.Term,
    predicate: RDF.Term,
    object: RDF.Term,
    graph: RDF.Term,
  ): {
    producer: BindingsProducer<TE>;
    componentOrder: QuadTermName[];
    index: IRdfStoreIndex<TE, boolean>;
  } | undefined {
    // Construct a quad pattern array
    const [ quadComponents ] =
      quadToPattern(subject, predicate, object, graph, this.indexesSupportQuotedPatterns);

    // Determine the best index for this pattern
    const indexWrapped = this.getBestIndexWrapped(quadComponents);

    // Re-order the quad pattern based on this best index's component order
    const quadComponentsOrdered = <QuadPatternTerms>
      orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadComponents);
    const ids = encodeOptionalTerms(quadComponentsOrdered, this.dictionary);

    // Abort if any of the terms does not exist in the dictionary
    if (!ids) {
      return undefined;
    }

    // Collect variables to bind
    const terms = orderQuadComponentsPermutation(
      indexWrapped.componentOrderPermutation,
      [ subject, predicate, object, graph ],
    );
    const variableIndexes: number[] = [];
    const variableIsQuad: boolean[] = [];
    let hasQuadVariables = false;
    for (let i = 0; i < terms.length; i++) {
      const termType = terms[i].termType;
      if (termType === 'Variable' || termType === 'Quad') {
        variableIndexes.push(i);
        const isQuad = termType === 'Quad';
        variableIsQuad.push(isQuad);
        hasQuadVariables = hasQuadVariables || isQuad;
      }
    }

    // Check if we need to do post-filtering for overlapping variables.
    // Overlapping variables are rare, so the per-component filter lists are only
    // materialized once an overlap is known to exist.
    let shouldFilterIndexes = false;
    for (let i = 0; i < terms.length && !shouldFilterIndexes; i++) {
      for (let j = i + 1; j < terms.length; j++) {
        if (terms[i].equals(terms[j])) {
          shouldFilterIndexes = true;
          break;
        }
      }
    }
    let filterIndexes: number[][] | undefined;
    if (shouldFilterIndexes) {
      filterIndexes = terms.map((variable, i) => {
        const equalVariables = [];
        for (let j = i + 1; j < terms.length; j++) {
          if (variable.equals(terms[j])) {
            equalVariables.push(j);
          }
        }
        return equalVariables;
      });
    }

    const source = indexWrapped.index
      .findEncoded(<EncodedQuadTerms<TE | undefined>> ids, quadComponentsOrdered);

    // Patterns without quoted triple patterns and without overlapping variables
    // never need post-filtering, and get a producer without any of that bookkeeping.
    if (!hasQuadVariables && filterIndexes === undefined) {
      return {
        producer: new BindingsProducer<TE>(bindingsFactory, source, this.dictionary, terms, variableIndexes),
        componentOrder: indexWrapped.componentOrder,
        index: indexWrapped.index,
      };
    }
    return {
      componentOrder: indexWrapped.componentOrder,
      index: indexWrapped.index,
      producer: new FilteringBindingsProducer<TE>(
        bindingsFactory,
        source,
        this.dictionary,
        terms,
        variableIndexes,
        this.dataFactory,
        variableIsQuad,
        filterIndexes,
      ),
    };
  }

  /**
   * Returns a generator producing all quads matching the pattern.
   * @param bindingsFactory The factory to use for creating bindings.
   * @param subject The subject, which can be a variable.
   * @param predicate The predicate, which can be a variable.
   * @param object The object, which can be a variable.
   * @param graph The graph, which can be a variable.
   */
  public* readBindings(
    bindingsFactory: RDF.BindingsFactory,
    subject: RDF.Term,
    predicate: RDF.Term,
    object: RDF.Term,
    graph: RDF.Term,
  ): IterableIterator<RDF.Bindings> {
    const prepared = this.prepareBindings(bindingsFactory, subject, predicate, object, graph);
    if (!prepared) {
      return;
    }
    const producer = prepared.producer;
    let bindings: RDF.Bindings | null = producer.read();
    while (bindings !== null) {
      yield bindings;
      bindings = producer.read();
    }
  }

  /**
   * Returns an array containing all bindings matching the pattern.
   * @param bindingsFactory The factory that will be used to create bindings.
   * @param subject The subject, which can be a variable.
   * @param predicate The predicate, which can be a variable.
   * @param object The object, which can be a variable.
   * @param graph The graph, which can be a variable.
   */
  public getBindings(
    bindingsFactory: RDF.BindingsFactory,
    subject: RDF.Term,
    predicate: RDF.Term,
    object: RDF.Term,
    graph: RDF.Term,
  ): RDF.Bindings[] {
    // This intentionally does not delegate to readBindings:
    // draining a generator costs an extra suspend/resume and an iterator result object per binding.
    const bindingsArray: RDF.Bindings[] = [];
    const prepared = this.prepareBindings(bindingsFactory, subject, predicate, object, graph);
    if (prepared) {
      const producer = prepared.producer;
      let bindings: RDF.Bindings | null = producer.read();
      while (bindings !== null) {
        bindingsArray.push(bindings);
        bindings = producer.read();
      }
    }
    return bindingsArray;
  }

  /**
   * Returns a stream that produces all quads matching the pattern.
   * @param bindingsFactory The factory that will be used to create bindings.
   * @param subject The subject, which can be a variable.
   * @param predicate The predicate, which can be a variable.
   * @param object The object, which can be a variable.
   * @param graph The graph, which can be a variable.
   */
  public matchBindings(
    bindingsFactory: RDF.BindingsFactory,
    subject: RDF.Term,
    predicate: RDF.Term,
    object: RDF.Term,
    graph: RDF.Term,
  ): AsyncIterator<RDF.Bindings> {
    const prepared = this.prepareBindings(bindingsFactory, subject, predicate, object, graph);
    return new BindingsIterator(
      prepared?.producer,
      prepared ?
        this.createSeeker(prepared.index, prepared.componentOrder, prepared.producer) :
        undefined,
      prepared ?
        this.resultOrderOf(prepared.index, prepared.componentOrder, subject, predicate, object, graph) :
        undefined,
    );
  }

  /**
   * Returns the number of distinct terms that exist in the store.
   *
   * @param terms An array of quad term names
   * @param filters An optional array of quad components that must be matched.
   */
  public countDistinctTerms(
    terms: QuadTermName[],
    filters?: (RDF.Term | undefined)[],
  ): number {
    // Determine the best index for this pattern, taking into account the components the filters bind,
    // as those must be matched before the counted terms can be read off the index
    const bestIndex = getBestIndexTerms(this.indexesWrappedComponentOrders, terms, filters);
    const indexWrapped = this.indexesWrapped[bestIndex];

    // Order terms, and keep index for fast inverse ordering during decoding
    const termOrderInToIndex: number[] = [];
    for (let i = 0; i < terms.length; i++) {
      termOrderInToIndex[i] = indexWrapped.componentOrder.indexOf(terms[i]);
    }
    const termsOrderedUnfiltered: (QuadTermName | undefined)[] = [ undefined, undefined, undefined, undefined ];
    for (let i = 0; i < terms.length; i++) {
      termsOrderedUnfiltered[termOrderInToIndex[i]] = terms[i];
    }
    const termsOrdered: QuadTermName[] = termsOrderedUnfiltered.filter(t => t !== undefined);

    // Determine path of terms to follow in the index
    let matchTerms = getIndexMatchTermsPath(indexWrapped.componentOrder, termsOrdered);

    // Encode filters if provided
    let filterTermsEncoded: EncodedQuadTerms<TE | undefined> | undefined;
    if (filters) {
      const encodedFilters = encodeAndExtendFilters(filters, matchTerms, indexWrapped.componentOrder, this.dictionary);
      if (encodedFilters === null) {
        // Filter term not in dictionary → no results
        return 0;
      }
      ({ filterTermsEncoded, matchTerms } = encodedFilters);
    }

    // Ensure distinctness: a depth that is neither counted nor pinned to a single term by a filter
    // fans out over the index, which would count the same term once per branch it occurs in
    if (matchTerms.some((matched, i) => !matched && filterTermsEncoded?.[i] === undefined)) {
      return this.getDistinctTerms(terms, filters).length;
    }

    // Call the best index's count method
    return indexWrapped.index.countTerms(matchTerms, filterTermsEncoded);
  }

  /**
   * Returns a generator producing distinct arrays of terms that exist in the store.
   * Each returned array corresponds to the terms specified by given quad term names.
   *
   * For example, when requesting the terms `[ 'subject', 'predicate' ]`,
   * a produced array could be `[ 'ex:s', 'ex:p' ]`,
   *
   * For example, if filters is in the form of `[ undefined, DF.namedNode('ex:p'), undefined, DF.defaultGraph() ]`,
   * this means that only those distinct terms must be returned if they originate from quads with predicate 'ex:p'
   * and in the default graph.
   *
   * @param terms An array of quad term names
   * @param filters An optional array of quad components that must be matched.
   */
  public* readDistinctTerms(
    terms: QuadTermName[],
    filters?: (RDF.Term | undefined)[],
  ): IterableIterator<RDF.Term[]> {
    // Determine the best index for this pattern
    const bestIndex = getBestIndexTerms(this.indexesWrappedComponentOrders, terms);
    const indexWrapped = this.indexesWrapped[bestIndex];

    // Order terms, and keep index for fast inverse ordering during decoding
    const termOrderInToIndex: number[] = [];
    for (let i = 0; i < terms.length; i++) {
      termOrderInToIndex[i] = indexWrapped.componentOrder.indexOf(terms[i]);
    }
    const termsOrderedUnfiltered: (QuadTermName | undefined)[] = [ undefined, undefined, undefined, undefined ];
    for (let i = 0; i < terms.length; i++) {
      termsOrderedUnfiltered[termOrderInToIndex[i]] = terms[i];
    }
    const termsOrdered: QuadTermName[] = termsOrderedUnfiltered.filter(t => t !== undefined);
    // For every output position, the position it has within the index-ordered results.
    const termOrderInToOrdered: number[] = [];
    for (let i = 0; i < termsOrdered.length; i++) {
      termOrderInToOrdered[terms.indexOf(termsOrdered[i])] = i;
    }

    // Determine path of terms to follow in the index
    let matchTerms = getIndexMatchTermsPath(indexWrapped.componentOrder, termsOrdered);

    // Encode filters if provided
    let filterTermsEncoded: EncodedQuadTerms<TE | undefined> | undefined;
    if (filters) {
      const encodedFilters = encodeAndExtendFilters(filters, matchTerms, indexWrapped.componentOrder, this.dictionary);
      if (encodedFilters === null) {
        // Filter term not in dictionary → no results
        return;
      }
      ({ filterTermsEncoded, matchTerms } = encodedFilters);
    }

    // Ensure distinctness (this can only occur when insufficient indexes are available,
    // or when filters go beyond match depths)
    // Distinctness is tracked on the *encoded* terms, in a trie of nested Maps.
    // Dictionaries encode every distinct term to a distinct value, so this is equivalent to
    // comparing the string representations of the decoded terms, but needs no string building at all.
    let distinctTerms: Map<TE, any> | undefined;
    if (matchTerms.includes(false)) {
      // TODO: if the first index level (e.g. graph) has just one term, distinctness is already guaranteed and we can
      //  skip this step. Also applies to countDistinctTerms.
      distinctTerms = new Map<TE, any>();
    }

    const dictionary = this.dictionary;

    // Call the best index's find method
    for (const readTerms of indexWrapped.index.findTerms(matchTerms, filterTermsEncoded)) {
      // Filter to ensure distinct terms are returned
      if (distinctTerms) {
        const lastI = readTerms.length - 1;
        let node = distinctTerms;
        for (let i = 0; i < lastI; i++) {
          // eslint-disable-next-line ts/no-unsafe-assignment
          let subNode = node.get(readTerms[i]);
          if (subNode === undefined) {
            subNode = new Map<TE, any>();
            node.set(readTerms[i], subNode);
          }
          // eslint-disable-next-line ts/no-unsafe-assignment
          node = subNode;
        }
        if (node.has(readTerms[lastI])) {
          continue;
        }
        node.set(readTerms[lastI], true);
      }

      // Decode terms, inversing the term ordering along the way.
      // The results are pushed in output order so that a packed array is built in one pass.
      const decodedTerms: RDF.Term[] = [];
      for (const orderedI of termOrderInToOrdered) {
        decodedTerms.push(dictionary.decode(readTerms[orderedI]));
      }

      yield decodedTerms;
    }
  }

  /**
   * Returns an array with distinct arrays of terms that exist in the store.
   * Each returned array corresponds to the terms specified by given quad term names.
   *
   * For example, when requesting the terms `[ 'subject', 'predicate' ]`,
   * a produced array could be `[ 'ex:s', 'ex:p' ]`,
   *
   * For example, if filters is in the form of `[ undefined, DF.namedNode('ex:p'), undefined, DF.defaultGraph() ]`,
   * this means that only those distinct terms must be returned if they originate from quads with predicate 'ex:p'
   * and in the default graph.
   *
   * @param terms An array of quad term names
   * @param filters An optional array of quad components that must be matched.
   */
  public getDistinctTerms(
    terms: QuadTermName[],
    filters?: (RDF.Term | undefined)[],
  ): RDF.Term[][] {
    const results: RDF.Term[][] = [];
    for (const result of this.readDistinctTerms(terms, filters)) {
      results.push(result);
    }
    return results;
  }

  /**
   * Returns a stream with distinct arrays of terms that exist in the store.
   * Each returned array corresponds to the terms specified by given quad term names.
   *
   * For example, when requesting the terms `[ 'subject', 'predicate' ]`,
   * a produced array could be `[ 'ex:s', 'ex:p' ]`,
   *
   * For example, if filters is in the form of `[ undefined, DF.namedNode('ex:p'), undefined, DF.defaultGraph() ]`,
   * this means that only those distinct terms must be returned if they originate from quads with predicate 'ex:p'
   * and in the default graph.
   *
   * @param terms An array of quad term names
   * @param filters An optional array of quad components that must be matched.
   */
  public matchDistinctTerms(
    terms: QuadTermName[],
    filters?: (RDF.Term | undefined)[],
  ): AsyncIterator<RDF.Term[]> {
    return wrap(this.readDistinctTerms(terms, filters));
  }

  /**
   * Returns the number of nodes in the given graph (can be a variable).
   * Nodes are all terms that are either a subject or object within the store.
   *
   * This method can only be called when the store is constructed with `indexNodes: true`.
   */
  public countNodes(graph: RDF.Term): number {
    if (!this.indexNodes) {
      throw new Error(`Nodes can only be read when the store was constructed with 'indexNodes: true'`);
    }
    if (graph.termType === 'Variable') {
      let size = 0;
      for (const graphIndex of this.indexNodes.values()) {
        size += graphIndex.size;
      }
      return size;
    }
    const graphEncoded = this.dictionary.encodeOptional(graph);
    if (graphEncoded !== undefined) {
      return this.indexNodes.get(graphEncoded)!.size;
    }
    return 0;
  }

  /**
   * Returns a generator producing all nodes in the given graph (can be a variable).
   * Nodes are all terms that are either a subject or object within the store.
   *
   * This method can only be called when the store is constructed with `indexNodes: true`.
   *
   * @param graph The graph to read the nodes from, or a variable if all graphs need to be considered.
   *
   * @returns a generator of tuples containing the named graph as first element and the node term as second element.
   */
  public* readNodes(graph: RDF.Term): IterableIterator<[ RDF.Term, RDF.Term ]> {
    if (!this.indexNodes) {
      throw new Error(`Nodes can only be read when the store was constructed with 'indexNodes: true'`);
    }

    // Decode nodes of all graphs if variable, or only the given graphs.
    if (graph.termType === 'Variable') {
      for (const entry of this.indexNodes.entries()) {
        const graphDecoded = this.dictionary.decode(entry[0]);
        for (const term of entry[1]) {
          yield [ graphDecoded, this.dictionary.decode(term) ];
        }
      }
    } else {
      const graphEncoded = this.dictionary.encodeOptional(graph);
      if (graphEncoded !== undefined) {
        const graphIndex = this.indexNodes.get(graphEncoded)!;
        for (const term of graphIndex) {
          yield [ graph, this.dictionary.decode(term) ];
        }
      }
    }
  }

  /**
   * Returns an array containing all nodes in the given graph (can be a variable).
   * Nodes are all terms that are either a subject or object within the store.
   *
   * This method can only be called when the store is constructed with `indexNodes: true`.
   *
   * @param graph The graph to read the nodes from, or a variable if all graphs need to be considered.
   *
   * @returns an array of tuples containing the named graph as first element and the node term as second element.
   */
  public getNodes(graph: RDF.Term): [ RDF.Term, RDF.Term ][] {
    return [ ...this.readNodes(graph) ];
  }

  /**
   * Returns a stream containing all nodes in the given graph (can be a variable).
   * Nodes are all terms that are either a subject or object within the store.
   *
   * This method can only be called when the store is constructed with `indexNodes: true`.
   *
   * @param graph The graph to read the nodes from, or a variable if all graphs need to be considered.
   *
   * @returns a stream of tuples containing the named graph as first element and the node term as second element.
   */
  public matchNodes(graph: RDF.Term): AsyncIterator<[ RDF.Term, RDF.Term ]> {
    if (!this.indexNodes) {
      throw new Error(`Nodes can only be read when the store was constructed with 'indexNodes: true'`);
    }

    return wrap(this.readNodes(graph));
  }

  /**
   * Returns the exact cardinality of the quads matching the pattern.
   * @param subject The optional subject.
   * @param predicate The optional predicate.
   * @param object The optional object.
   * @param graph The optional graph.
   */
  public countQuads(
    subject?: RDF.Term | null,
    predicate?: RDF.Term | null,
    object?: RDF.Term | null,
    graph?: RDF.Term | null,
  ): number {
    // Construct a quad pattern array
    const [ quadComponents ] =
      quadToPattern(subject, predicate, object, graph, this.indexesSupportQuotedPatterns);

    // Optimize all-variables pattern
    if (quadComponents.every(quadComponent => quadComponent === undefined)) {
      return this.size;
    }

    // Determine the best index for this pattern
    const indexWrapped = this.getBestIndexWrapped(quadComponents);

    // Re-order the quad pattern based on this best index's component order
    const quadComponentsOrdered = <QuadPatternTerms>
      orderQuadComponentsPermutation(indexWrapped.componentOrderPermutation, quadComponents);

    // Call the best index's count method.
    return indexWrapped.index.count(quadComponentsOrdered);
  }

  /**
   * Wrap this store inside a DatasetCore interface.
   * Any mutations in either this store or the wrapper will propagate to each other.
   */
  public asDataset(): DatasetCoreWrapper<TE, TQ> {
    return new DatasetCoreWrapper(this);
  }
}

export interface IRdfStoreIndexWrapped<TE> {
  componentOrder: QuadTermName[];
  /**
   * The precomputed permutation of SPOG indexes corresponding to `componentOrder`.
   */
  componentOrderPermutation: number[];
  componentOrderInverse: Record<QuadTermName, number>;
  index: IRdfStoreIndex<TE, boolean>;
}
